import type { ReactNode } from 'react';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * L'OSD del lettore, nella forma del prototipo.
 *
 * ## Cos'era prima
 *
 * Una targhetta in basso a sinistra con il nome del canale e «3 di 60». Il prototipo ha invece
 * quattro cose che non c'erano:
 *
 *  1. un **distintivo** in alto a sinistra che dice cos'è — diretta, replica, video — accanto alla
 *     riga che ricorda cosa fanno i tasti;
 *  2. l'**orologio** in alto a destra;
 *  3. in basso, sopra una sfumatura, il canale e il **titolo del programma** a 64 px;
 *  4. la **barra di avanzamento** del programma con gli orari ai due capi, e i tasti delle azioni.
 *
 * Non è rifinitura: il titolo del programma e gli orari sono l'informazione per cui si apre l'OSD.
 * Un lettore che dice solo il nome del canale obbliga ad aprire la guida per sapere cosa si sta
 * guardando — che è la ragione per cui esiste l'OSD.
 *
 * ## Perché la sfumatura è una variabile
 *
 * `--osd`: l'impostazione «Effetti visivi» la spegne davvero, sostituendola con un fondo pieno. «Ogni
 * voce deve avere un effetto misurabile. Se spegnere gli effetti non cambia nulla, la voce va tolta»
 * (07-PRESTAZIONI §5) — e su una Fire TV Stick una sfumatura a tutta larghezza ridisegnata a ogni
 * secondo si sente.
 */

export interface PlayerOverlayProps {
  /** Numero e nome del canale: la riga piccola sopra il titolo. */
  readonly channelLine: string;
  /**
   * Il titolo del programma in onda, o `null` quando l'EPG non lo sa.
   *
   * `null` **non** diventa il nome del canale: sarebbe una risposta plausibile alla domanda
   * sbagliata, e chi legge crederebbe che il programma si chiami come l'emittente. Quando manca, al
   * suo posto va il nome del canale scritto come tale — lo decide chi chiama, che sa perché manca.
   */
  readonly programTitle: string | null;
  /** Gli orari ai capi della barra, già formattati. Assenti quando non c'è un programma. */
  readonly startLabel?: string;
  readonly endLabel?: string;
  /** Quanto è passato del programma, 0–100. `null` quando non si sa: la barra non si disegna. */
  readonly percent: number | null;
  /** Cosa viene dopo, quando lo si sa. */
  readonly nextLabel?: string;
  /** I tasti delle azioni, nell'ordine del disegno. */
  readonly actions: ReadonlyArray<{ readonly id: string; readonly label: string; readonly onSelect: () => void }>;
  /** Quale azione ha il fuoco, o `null` quando i tasti sono di qualcos'altro. */
  readonly focusedAction: string | null;
  /**
   * Una riga d'avviso sopra il titolo: al momento serve per l'audio spento dalla piattaforma.
   *
   * Non è un errore e non va dove vanno gli errori — si sta **guardando**. È una cosa che manca, e
   * accanto c'è il tasto che la rimedia: un avviso senza il suo rimedio è solo una lamentela.
   */
  readonly notice?: string;
  /**
   * Si tocca invece di premere: misure da telefono.
   *
   * Non è la stessa fascia rimpicciolita. Il titolo a 64 px su 390 px va a capo tre volte e la fascia
   * si prende **tutta la pagina**; e i tasti cambiano bersaglio, non solo dimensione — 68 px servono
   * a un contorno di fuoco visto da tre metri, 44 sono il minimo sotto il quale un dito sbaglia.
   */
  readonly touch?: boolean;
}

/**
 * Le misure, in due taglie.
 *
 * ## Perché due e non una elastica
 *
 * Perché non è la stessa fascia rimpicciolita: a 64 px un titolo lungo su 390 px va a capo tre volte
 * e la fascia si mangia **tutta la pagina** — che è quel che succedeva. E i tasti cambiano bersaglio,
 * non solo dimensione: 68 px d'altezza sono giusti per un contorno di fuoco visto da tre metri, 44
 * sono il minimo sotto il quale un dito sbaglia.
 *
 * Due tabelle esplicite e non un `clamp()` nel mezzo: una misura che scala con la larghezza passa per
 * tutte le taglie intermedie, e nessuna di quelle è stata guardata da nessuno.
 */
const TAGLIE = {
  remoto: {
    padding: '120px 72px 60px',
    gap: 18,
    canale: 26,
    avviso: 20,
    titolo: 64,
    titoloAssente: 28,
    orari: 22,
    tastoAltezza: 68,
    tastoPadding: '0 30px',
    tastoTesto: 25,
    successivo: 24,
  },
  dito: {
    // Il respiro in basso tiene conto della barra di casa: `env()` è zero dove non c'è.
    padding: `48px 16px calc(14px + env(safe-area-inset-bottom, 0px))`,
    gap: 8,
    canale: 13,
    avviso: 12,
    titolo: 20,
    titoloAssente: 14,
    orari: 12,
    tastoAltezza: 44,
    tastoPadding: '0 14px',
    tastoTesto: 14,
    successivo: 12,
  },
} as const;

export function PlayerOverlay({
  channelLine,
  programTitle,
  startLabel,
  endLabel,
  percent,
  nextLabel,
  actions,
  focusedAction,
  notice,
  touch = false,
}: PlayerOverlayProps): ReactNode {
  const translator = useTranslator();
  const m = touch ? TAGLIE.dito : TAGLIE.remoto;

  return (
    <div
      data-osd="1"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        /*
          **Sopra la superficie del tocco, o i tasti non si possono premere.**

          Col dito la diretta mette sotto la fascia un rettangolo invisibile che copre il video: serve
          a richiamare i comandi dopo che si sono ritirati. Quel rettangolo è posizionato con
          `zIndex: 1`, e questa fascia non dichiarava niente — cioè `auto`, che perde contro
          qualunque numero, **anche comparendo dopo nell'albero**.

          Il risultato: ogni tocco finiva sulla superficie invisibile. I tasti rispondevano al
          passaggio del dito e non alla pressione, e l'unico effetto visibile era la fascia che si
          chiudeva. Un difetto che non solleva niente e che a parole si racconta «i tasti non vanno».
        */
        zIndex: 4,
      }}
    >
      {/*
        **Niente fascia in alto mentre si guarda.**

        C'erano il distintivo rosso «IN DIRETTA», il promemoria dei tasti e l'orologio. Tutti e tre
        dicono cose che chi guarda sa già: che è la diretta lo sa perché ha scelto un canale, i tasti
        li ha imparati alla seconda pressione, e l'ora ce l'ha il televisore. Sono tre rettangoli
        sopra l'immagine che non portano niente — e su un canale sportivo coprono proprio l'angolo
        dove stanno punteggio e cronometro.
      */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: m.padding,
          background: 'var(--osd, linear-gradient(0deg,rgba(5,7,10,.96) 30%,rgba(5,7,10,0)))',
          display: 'flex',
          flexDirection: 'column',
          gap: m.gap,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: touch ? 8 : 16 }}>
          <span style={{ font: `600 ${m.canale}px ${FONT_STACK}`, color: colors.textMuted }}>{channelLine}</span>
          {notice !== undefined && (
            <span
              data-osd-notice="1"
              style={{
                padding: touch ? '3px 8px' : '6px 14px',
                borderRadius: 8,
                // Ambra e non rosso: è una cosa che manca, non una cosa rotta. Il rosso su un canale
                // che si sta guardando bene fa cercare un guasto che non c'è.
                background: colors.surfaceRaised,
                color: colors.accent,
                font: `600 ${m.avviso}px ${FONT_STACK}`,
              }}
            >
              {notice}
            </span>
          )}
        </div>
        {/*
          Il titolo è grande **quando c'è un titolo**.

          «Programma non disponibile» a 64 px in grassetto occupa mezzo schermo per dire che non si sa
          niente: l'assenza gridata più forte del contenuto. Quando manca si scrive piccolo e smorzato
          — è una nota, non un titolo.
        */}
        <div
          data-osd-title="1"
          data-osd-title-kind={programTitle === null ? 'absent' : 'program'}
          style={
            programTitle === null
              ? { font: `500 ${m.titoloAssente}px/1.2 ${FONT_STACK}`, color: colors.textDim }
              : { font: `800 ${m.titolo}px/1.1 ${FONT_STACK}`, letterSpacing: '-.02em' }
          }
        >
          {programTitle ?? translator.t('player.noProgram')}
        </div>

        {/*
          La barra si disegna solo quando c'è un programma da misurare.

          Una barra a zero su un canale senza EPG non dice «non lo so»: dice «è appena cominciato», ed
          è una bugia che si tiene in piedi da sola perché è plausibile.
        */}
        {percent !== null && startLabel !== undefined && endLabel !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: touch ? 10 : 24 }}>
            <span style={{ font: `600 ${m.orari}px ${FONT_STACK}`, color: colors.textMuted }}>{startLabel}</span>
            <div
              data-osd-progress={String(Math.round(percent))}
              style={{ flex: 1, height: 8, borderRadius: 4, background: colors.border, overflow: 'hidden' }}
            >
              <div style={{ height: '100%', width: `${percent}%`, background: colors.accent }} />
            </div>
            <span style={{ font: `600 ${m.orari}px ${FONT_STACK}`, color: colors.textMuted }}>{endLabel}</span>
          </div>
        )}

        {/*
          I tasti sono premibili, quindi qui `pointerEvents` torna in gioco: lo strato esterno lo
          spegne perché non deve intercettare niente, ma un tasto che non si può premere è peggio di
          un tasto che non c'è.
        */}
        <div style={{ display: 'flex', alignItems: 'center', gap: touch ? 8 : 20, paddingTop: touch ? 2 : 8, pointerEvents: 'auto', flexWrap: touch ? 'wrap' : 'nowrap' }}>
          {actions.map((action) => {
            const focused = focusedAction === action.id;
            return (
              <button
                key={action.id}
                type="button"
                data-f="1"
                data-osd-action={action.id}
                data-focused={focused ? '1' : '0'}
                onClick={action.onSelect}
                style={{
                  height: m.tastoAltezza,
                  padding: m.tastoPadding,
                  border: 0,
                  margin: 0,
                  borderRadius: 14,
                  cursor: 'pointer',
                  background: colors.surfaceRaised,
                  color: colors.text,
                  font: `600 ${m.tastoTesto}px ${FONT_STACK}`,
                  outline: focused ? `4px solid ${colors.accent}` : 'none',
                  outlineOffset: 4,
                  transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
                  transition: 'var(--tr, transform .16s ease)',
                }}
              >
                {action.label}
              </button>
            );
          })}
          {nextLabel !== undefined && (
            <span style={{ marginLeft: 'auto', font: `500 ${m.successivo}px ${FONT_STACK}`, color: colors.textDim }}>
              {nextLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
